import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  Legend,
  Line,
  ReferenceLine,
} from "recharts";
import { Box } from "@mui/material";
import {
  getClickedTMs,
  renderMarkers,
  ChartBandZoomButton,
  axisTickFormatter,
  fmtDateTime,
} from "../../../common/chartZoom";

const COLORS = [
  "#1976d2",
  "#2e7d32",
  "#d32f2f",
  "#6a1b9a",
  "#ff8f00",
  "#0097a7",
];

export type WaterFlowChartProps = {
  data: any[];
  selected: string[]; // q02..q07
  domain: [number, number] | null;
  markers?: number[];
  onChartClick?: (t: number) => void;
  onZoom?: () => void;
  labelFor?: (k: string) => string;
  /** Line colors by position in `selected`. Defaults to the Q palette. */
  colors?: string[];
};

const WaterFlowChart: React.FC<WaterFlowChartProps> = ({
  data,
  selected,
  domain,
  markers = [],
  onChartClick,
  onZoom,
  labelFor = (k) => k.toUpperCase(),
  colors = COLORS,
}) => {
  const handleClick = (state: any) => {
    if (!onChartClick) return;
    const t = getClickedTMs(state);
    if (t != null) onChartClick(t);
  };

  const sel = selected.length ? selected : ["q02"];

  return (
    <Box sx={{ height: 260, position: "relative" }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ left: 60, right: 8, top: 10, bottom: 8 }}
          onClick={handleClick}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            type="number"
            dataKey="tMs"
            domain={domain ?? (["auto", "auto"] as any)}
            tickFormatter={axisTickFormatter(domain)}
            minTickGap={20}
          />
          <YAxis
            type="number"
            domain={[0, 1]}
            ticks={[0, 1]}
            width={56}
            tickFormatter={(v) => (Number(v) >= 0.5 ? "ON" : "OFF")}
          />
          <RTooltip
            labelFormatter={(t) => fmtDateTime(Number(t))}
            formatter={(v, name) => [
              Number(v) >= 0.5 ? "ON" : "OFF",
              labelFor(String(name)), // <-- cast to string
            ]}
          />

          <Legend />

          {/* Midline between states */}
          <ReferenceLine y={0.5} stroke="#9e9e9e" strokeDasharray="3 3" />

          {sel.map((k, i) => (
            <Line
              key={k}
              type="stepAfter"
              dataKey={k}
              name={labelFor(k)}
              stroke={colors[i % colors.length]}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          ))}

          {renderMarkers(markers)}
        </LineChart>
      </ResponsiveContainer>
      <ChartBandZoomButton domain={domain} markers={markers} onZoom={onZoom} />
    </Box>
  );
};

export default React.memo(WaterFlowChart);
